import { useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Alert } from '../components/Alert';
import { AuthLayout } from '../components/AuthLayout';
import { Button } from '../components/Button';
import { Field } from '../components/Field';
import { Turnstile } from '../components/Turnstile';
import { api, queryKeys } from '../lib/api';
import { useAuth } from '../lib/auth';
import { isApiCode, messageForError } from '../lib/errors';
import { parseForm, registerFormSchema, type FieldErrors } from '../lib/forms';
import { describeDevice } from '../lib/passkeys';

type Keys = 'email' | 'password' | 'displayName' | 'householdName' | 'inviteCode';

const CAPTCHA_CODES = ['CAPTCHA_REQUIRED', 'CAPTCHA_FAILED', 'CAPTCHA_UNAVAILABLE'];

export function RegisterPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [mode, setMode] = useState<'create' | 'join'>('create');
  const [errors, setErrors] = useState<FieldErrors<Keys>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaReset, setCaptchaReset] = useState(0);
  const [captchaBroken, setCaptchaBroken] = useState(false);

  // Which captcha (if any) the server wants; the form stays disabled until we know.
  const config = useQuery({
    queryKey: queryKeys.authConfig,
    queryFn: () => api.auth.config(),
    staleTime: 5 * 60_000,
  });
  const captcha = config.data?.captcha;
  const needsCaptcha = captcha?.provider === 'turnstile';
  const canSubmit = !busy && config.isSuccess && (!needsCaptcha || captchaToken !== null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const r = parseForm(registerFormSchema, e.currentTarget);
    if (r.errors) return setErrors(r.errors as FieldErrors<Keys>);
    setErrors({});
    setError(null);
    setBusy(true);
    try {
      await api.auth.register({ ...r.data, ...(captchaToken ? { captchaToken } : {}) });
      // Registration may or may not start a session; sign in explicitly so the flow is deterministic.
      const me = await refresh();
      if (!me) {
        await api.auth.login({
          email: r.data.email,
          password: r.data.password,
          deviceName: describeDevice(),
        });
        await refresh();
      }
      // The account exists but is locked until the mailed link is opened (D25).
      void navigate('/confirm-email', { replace: true });
    } catch (err) {
      setError(messageForError(err));
      if (CAPTCHA_CODES.some((c) => isApiCode(err, c))) {
        // A token is single-use: ask for a fresh challenge before the next try.
        setCaptchaToken(null);
        setCaptchaReset((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout
      title="Create your account"
      subtitle="One account per person; recipes and lists are shared per household."
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {config.isError ? (
          <Alert tone="error">
            Sign-up isn’t available right now ({messageForError(config.error)}).{' '}
            <button
              type="button"
              onClick={() => void config.refetch()}
              className="font-medium underline underline-offset-2"
            >
              Try again
            </button>
          </Alert>
        ) : null}
        <Field
          id="displayName"
          name="displayName"
          label="Your name"
          autoComplete="name"
          required
          error={errors.displayName}
        />
        <Field
          id="email"
          name="email"
          label="Email"
          type="email"
          autoComplete="username"
          inputMode="email"
          required
          error={errors.email}
        />
        <p className="-mt-2 text-xs text-muted">
          We’ll send a confirmation link here; the account stays locked until you open it.
        </p>
        <Field
          id="password"
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          minLength={10}
          error={errors.password}
        />
        <p className="-mt-2 text-xs text-muted">
          At least 10 characters. A short sentence works well.
        </p>

        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Household</legend>
          <div className="grid grid-cols-2 gap-2" role="radiogroup">
            {(['create', 'join'] as const).map((m) => (
              <label
                key={m}
                className={`flex min-h-11 cursor-pointer items-center justify-center rounded-lg border px-3 text-sm ${
                  mode === m ? 'border-accent bg-accent-soft text-brand' : 'border-line bg-surface'
                }`}
              >
                <input
                  type="radio"
                  name="mode"
                  value={m}
                  checked={mode === m}
                  onChange={() => setMode(m)}
                  className="sr-only"
                />
                {m === 'create' ? 'Start a new household' : 'I have an invite code'}
              </label>
            ))}
          </div>
          {mode === 'create' ? (
            <Field
              id="householdName"
              name="householdName"
              label="Household name"
              placeholder="The Smiths"
              required
              error={errors.householdName}
            />
          ) : (
            <Field
              id="inviteCode"
              name="inviteCode"
              label="Invite code"
              autoCapitalize="characters"
              autoCorrect="off"
              required
              error={errors.inviteCode}
            />
          )}
        </fieldset>

        {needsCaptcha && captcha ? (
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium text-ink">Quick check that you’re a person</span>
            <Turnstile
              siteKey={captcha.siteKey ?? ''}
              onToken={(t) => {
                setCaptchaToken(t);
                if (t) setCaptchaBroken(false);
              }}
              onLoadError={() => setCaptchaBroken(true)}
              resetKey={captchaReset}
              className="min-h-16"
            />
            {captchaBroken ? (
              <p role="alert" className="text-sm text-fail">
                The captcha couldn’t load. Check that nothing is blocking challenges.cloudflare.com,
                then reload the page.
              </p>
            ) : null}
          </div>
        ) : null}

        <Button type="submit" disabled={!canSubmit}>
          {busy ? 'Creating…' : config.isPending ? 'Loading…' : 'Create account'}
        </Button>
      </form>
      <p className="text-center text-sm text-muted">
        Already have an account?{' '}
        <Link to="/login" className="font-medium text-brand underline-offset-2 hover:underline">
          Sign in
        </Link>
      </p>
    </AuthLayout>
  );
}
